import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { NetworkCanvas } from "./NetworkCanvas";
import { XMatrix } from "./XMatrix";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TICKER = ["NEED × OPPORTUNITY", "PERSON × PERSON", "PROBLEM × SOLUTION"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const xY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const xOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-grid"
    >
      <NetworkCanvas className="absolute inset-0 w-full h-full opacity-60" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 72% 42%, rgba(0,240,255,0.09), transparent 62%), radial-gradient(ellipse 45% 40% at 18% 82%, rgba(138,43,226,0.12), transparent 62%)",
        }}
      />

      <div className="absolute right-[-28%] sm:right-[-12%] lg:right-[-3%] top-1/2 -translate-y-1/2 w-[95vw] sm:w-[70vw] lg:w-[44vw] max-w-[760px] opacity-25 lg:opacity-100 pointer-events-none">
        <motion.div style={{ y: xY, opacity: xOpacity }} data-testid="hero-x-matrix">
          <XMatrix />
        </motion.div>
      </div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28 sm:pt-36 pb-24 sm:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 sm:mb-10"
        >
          <span data-testid="hero-eyebrow" className="font-mono-accent text-[11px] sm:text-xs tracking-[0.32em] text-[#00F0FF]">
            /// FINDXCHANGE
          </span>
          <span className="h-px w-14 bg-[#00F0FF]/40" />
          <span data-testid="hero-tagline" className="font-mono-accent text-[11px] sm:text-xs tracking-[0.32em] text-slate-500">
            FIND. CONNECT. GROW.
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[clamp(1.35rem,6.4vw,2rem)] sm:text-6xl lg:text-7xl xl:text-[5rem] text-slate-100 max-w-4xl break-words"
        >
          <MaskedLine delay={0.3}>Find what you need.</MaskedLine>
          <MaskedLine delay={0.46}>
            Connect with the <span className="text-[#00F0FF]">right people</span>.
          </MaskedLine>
          <MaskedLine delay={0.62}>
            Grow from the{" "}
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(92deg, #00F0FF 10%, #8A2BE2 90%)" }}>
              connection
            </span>.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          data-testid="hero-subtext"
          className="mt-6 sm:mt-9 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          People have what other people need. FINDXCHANGE helps connect the dots — one global
          network for people, businesses, skills, resources, opportunities and solutions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-8 sm:mt-11 flex flex-col sm:flex-row gap-4"
        >
          <a href="/post?type=need" data-testid="hero-need-something-btn" className="btn-primary">
            I Need Something
          </a>
          <a href="/post?type=offer" data-testid="hero-offer-something-btn" className="btn-ghost">
            I Can Offer Something
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-10 sm:mt-16 flex flex-wrap gap-x-8 gap-y-3"
          data-testid="hero-ticker"
        >
          {TICKER.map((t) => (
            <span key={t} className="font-mono-accent text-[11px] tracking-[0.25em] text-slate-600">
              {t}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.a
        href="#exchange"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600 hover:text-[#00F0FF] transition-colors"
      >
        <span className="font-mono-accent text-[10px] tracking-[0.35em]">SCROLL</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.a>
    </section>
  );
};
